import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import StudentPage from '../components/StudentPage';
import dayjs from 'dayjs';
import advancedFormat from "dayjs/plugin/advancedFormat";
import useAuthContext from 'hooks/useAuthContext';
import useFeedbackDialog from 'hooks/useFeedbackDialog';
import Stack from '@mui/material/Stack';
import ProgressCard from './components/ProgressCard/ProgressCard';
import StudentPeriod from './components/StudentPeriod/StudentPeriod';
import { useCancelPeriodEnrollment } from 'api/hooks/private/periods/CancelPeriodEnrollment';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import useStudentContext from 'hooks/useStudentContext';
import useConfirmDialog from 'hooks/useConfirmDialog';

function StudentDashboard(): JSX.Element {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const { showFeedbackDialog } = useFeedbackDialog();
  const { showConfirmDialog } = useConfirmDialog();
  // const queryClient = useQueryClient();
  const { student, periodEnrollments, loadingPeriodEnrollments } = useStudentContext();

  const cancelPeriodEnrollment = useCancelPeriodEnrollment({
    onSuccess: (data) => {
      if (data) {
        showFeedbackDialog({
          icon: 'success',
          title: 'Course period has been canceled.',
        });
      }
    },
    onError(error: Error) {
      showFeedbackDialog({
        title: 'Error',
        message: error.message
      });
    },
  });

  dayjs.extend(advancedFormat);

  const cancel = async (id: string) => {
    await cancelPeriodEnrollment.mutateAsync({
      coursePeriodId: id,
      studentId: studentId ?? '',
      cancel: true
    });
  }

  return (
    <StudentPage
      banner={<ProgressCard />}
      title={`Welcome ${student?.nickname ?? student?.first_name}`}
    >
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flex={'1 1 100%'}
        justifyContent={{ sm: 'space-between' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        marginBottom={4}
      >
        <Box marginBottom={{ xs: 1, sm: 0 }}>
          <Box marginBottom={2}>
            <Typography variant={'h6'} fontWeight={700}>
              Your Course Periods
            </Typography>
          </Box>
        </Box>
      </Box>

      <Stack direction="column" spacing={2}>
        {!loadingPeriodEnrollments && periodEnrollments?.map((enrollment) => (
          <StudentPeriod
            key={enrollment?.course_period?.id}
            id={enrollment?.course_period?.id}
            start={enrollment?.course_period?.start}
            end={enrollment?.course_period?.end}
            showDate={true}
            courseTitle={enrollment?.course_period?.course_section?.course?.title}
            sectionTitle={enrollment?.course_period?.course_section?.title}
            attendance={enrollment?.attendance}
            canceledAt={enrollment?.canceled_at}
            allowCancellation={enrollment?.course_enrollment?.course_policy?.allow_cancellations ?? false}
            lateCancellationDeadline={enrollment?.course_enrollment?.course_policy?.late_cancellation_deadline}
            instructors={enrollment?.course_period?.instructors?.map(instructor => instructor?.formal_name ?? '')}
            location={enrollment?.course_period?.course_section?.tenant_location?.name}
            onClickCancel={(id: string) => {
              const policy = enrollment?.course_enrollment?.course_policy;
              const lateCancelDeadline = dayjs(enrollment?.course_period?.start).subtract(dayjs.duration(policy?.late_cancellation_deadline ?? {}));
              let feeMessage: JSX.Element | undefined = undefined;

              if ( // check if a late cancellation fee will be applied
                policy?.late_cancellation_deadline &&
                policy?.late_cancellation_fee &&
                policy?.late_cancellation_fee > 0 &&
                dayjs().isAfter(lateCancelDeadline)
              ) {
                feeMessage = <Typography variant="h6" align="center">This will result in a late cancellation fee of <b>${(policy?.late_cancellation_fee / 100).toFixed(2)}</b></Typography>;
              } else if ( // check if a  cancellation fee will be applied
                policy?.cancellation_fee &&
                policy?.cancellation_fee > 0
              ) {
                feeMessage = <Typography variant="h6" align="center">This will result in a cancellation fee of <b>${(policy?.cancellation_fee / 100).toFixed(2)}</b></Typography>;
              }

              showConfirmDialog({
                title: 'Are you sure you want to cancel this period?',
                message: feeMessage,
                cancelText: 'No',
                confirmText: 'Yes',
                danger: true,
              }).then((confirmed) => {
                if (confirmed) {
                  cancel(id);
                }
              });
            }}
          />
        ))}

        {loadingPeriodEnrollments && (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {periodEnrollments?.length === 0 && (
          <Typography align='center'>
            You are not enrolled in any course periods.
          </Typography>
        )}
      </Stack>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={cancelPeriodEnrollment?.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </StudentPage>

  );
}

export default StudentDashboard;

